import { useEffect, useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useNavigate } from "react-router-dom";

const useDebounced = (value: string, delay = 350) => {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
};

const ProductSearch = () => {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const dq = useDebounced(q);

  const { data, isFetching } = useQuery({
    queryKey: ["search-products", dq],
    queryFn: async () => {
      if (!dq.trim()) return [] as any[];
      const title = await supabase
        .from("products")
        .select("id, title, description, price_cents, currency, product_images(url, sort)")
        .ilike("title", `%${dq}%`)
        .eq("status", "active")
        .limit(12);
      const desc = await supabase
        .from("products")
        .select("id, title, description, price_cents, currency, product_images(url, sort)")
        .ilike("description", `%${dq}%`)
        .eq("status", "active")
        .limit(12);
      const rows = [...(title.data ?? []), ...(desc.data ?? [])];
      const dedup = new Map(rows.map((r: any) => [r.id, r]));
      return Array.from(dedup.values());
    },
  });

  const results = useMemo(() => (data ?? []).map((p: any) => ({
    id: p.id,
    title: p.title,
    price: `${p.currency} ${(p.price_cents/100).toFixed(2)}`,
    img: p.product_images?.[0]?.url as string | undefined,
  })), [data]);

  return (
    <section className="grid gap-4">
      <div className="max-w-xl mx-auto w-full">
        <Input
          placeholder="Search products..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="h-12 text-base"
        />
      </div>
      {dq && (
        <div className="container mx-auto px-0">
          {isFetching && <div className="text-sm text-muted-foreground text-center">Searching…</div>}
          {!isFetching && !results.length && (
            <div className="text-sm text-muted-foreground text-center">No results</div>
          )}
          {!!results.length && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
              {results.map((p) => (
                <div key={p.id} className="border rounded-xl p-3 hover:shadow-md transition cursor-pointer" onClick={() => navigate(`/products/${p.id}`)}>
                  <div className="aspect-square rounded-md overflow-hidden bg-muted mb-2">
                    {p.img ? (
                      <img src={p.img} alt={p.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-xs text-muted-foreground">No image</div>
                    )}
                  </div>
                  <div className="text-sm font-medium line-clamp-1">{p.title}</div>
                  <div className="text-xs text-muted-foreground">{p.price}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
};

export default ProductSearch;
